import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { INVOICE_STATUSES, type InvoiceDto, type InvoiceStatus } from '@shared';
import { apiGet, ApiError } from '../../lib/api';

type Filter = 'all' | InvoiceStatus;

const STATUS_LABEL: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  issued: 'Issued',
  awaiting_wire: 'Awaiting wire',
  received: 'Received',
  cleared: 'Cleared',
  void: 'Void',
  refunded: 'Refunded',
};

const STATUS_TONE: Record<InvoiceStatus, string> = {
  draft: '',
  issued: 'amber',
  awaiting_wire: 'amber',
  received: 'accent',
  cleared: 'green',
  void: 'neutral',
  refunded: 'red',
};

function formatMoney(amount_pence: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency, maximumFractionDigits: 0 })
      .format(amount_pence / 100);
  } catch {
    return `${currency} ${(amount_pence / 100).toLocaleString()}`;
  }
}

function isOutstanding(s: InvoiceStatus): boolean {
  return s === 'issued' || s === 'awaiting_wire' || s === 'received';
}

export function AdminFinance() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<InvoiceDto[] | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiGet<{ invoices: InvoiceDto[] }>('/api/invoices')
      .then((r) => setRows(r.invoices))
      .catch((err: Error) => setError(err instanceof ApiError ? err.serverMessage : err.message));
  }, []);

  const counts = useMemo(() => {
    const c: Record<InvoiceStatus, number> = {
      draft: 0, issued: 0, awaiting_wire: 0, received: 0, cleared: 0, void: 0, refunded: 0,
    };
    for (const r of rows ?? []) c[r.status] = (c[r.status] ?? 0) + 1;
    return c;
  }, [rows]);

  const outstandingByCurrency = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows ?? []) {
      if (!isOutstanding(r.status)) continue;
      m.set(r.currency, (m.get(r.currency) ?? 0) + r.amount_pence);
    }
    return m;
  }, [rows]);

  // Card-vs-wire breakdown among cleared invoices in the last 30 days. Drives
  // the "By rail" card. Phase 5e: useful at-a-glance for Finance to see how
  // many clients are paying via Revolut vs bank transfer.
  const clearedByRail = useMemo(() => {
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    let card = 0;
    let wire = 0;
    for (const r of rows ?? []) {
      if (r.status !== 'cleared') continue;
      if (r.paid_at && new Date(r.paid_at).getTime() < cutoff) continue;
      if (r.payment_rail === 'card') card += 1;
      else if (r.payment_rail === 'bank') wire += 1;
    }
    return { card, wire };
  }, [rows]);

  const list = useMemo(
    () => (rows ?? []).filter((r) => (filter === 'all' ? true : r.status === filter)),
    [rows, filter],
  );

  return (
    <>
      <div>
        <h1 className="portal-h1">Finance</h1>
        <p className="portal-sub">
          Issued invoices, payments awaiting reconciliation, and cleared revenue. Issue an invoice
          from an accepted proposal in <Link to="/admin/proposals" style={{ borderBottom: '1px solid var(--rule)' }}>proposals</Link>.
        </p>
      </div>

      <div className="portal-cards">
        <div className="card">
          <div className="lbl">Outstanding</div>
          <div className="val">{counts.issued + counts.awaiting_wire + counts.received}</div>
          <div className="delta">Open invoices</div>
        </div>
        <div className="card">
          <div className="lbl">Cleared</div>
          <div className="val">{counts.cleared}</div>
          <div className="delta">Reconciled</div>
        </div>
        <div className="card">
          <div className="lbl">Voided / refunded</div>
          <div className="val">{counts.void + counts.refunded}</div>
          <div className="delta">Closed without payment</div>
        </div>
        <div className="card">
          <div className="lbl">Cleared by rail · 30d</div>
          <div style={{ fontSize: 14, marginTop: 4 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontVariantNumeric: 'tabular-nums' }}>
              <span style={{ color: 'var(--fg-mute)' }}>Card</span>
              <strong>{clearedByRail.card}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontVariantNumeric: 'tabular-nums' }}>
              <span style={{ color: 'var(--fg-mute)' }}>Wire</span>
              <strong>{clearedByRail.wire}</strong>
            </div>
            {clearedByRail.card === 0 && clearedByRail.wire === 0 && (
              <span style={{ color: 'var(--fg-mute)', fontSize: 12 }}>No payments in window</span>
            )}
          </div>
        </div>
        <div className="card">
          <div className="lbl">By currency</div>
          <div style={{ fontSize: 14, marginTop: 4 }}>
            {Array.from(outstandingByCurrency.entries()).map(([ccy, amt]) => (
              <div key={ccy} style={{ display: 'flex', justifyContent: 'space-between', fontVariantNumeric: 'tabular-nums' }}>
                <span style={{ color: 'var(--fg-mute)' }}>{ccy}</span>
                <strong>{formatMoney(amt, ccy)}</strong>
              </div>
            ))}
            {outstandingByCurrency.size === 0 && (
              <span style={{ color: 'var(--fg-mute)', fontSize: 12 }}>Nothing outstanding</span>
            )}
          </div>
        </div>
      </div>

      <div className="tabs" style={{ marginTop: 24 }}>
        {(['all', ...INVOICE_STATUSES] as Filter[]).map((t) => (
          <button key={t} className={filter === t ? 'on' : ''} onClick={() => setFilter(t)}>
            {t === 'all' ? 'All' : STATUS_LABEL[t]}
          </button>
        ))}
      </div>

      {error && (
        <div className="card" style={{ marginTop: 16, borderColor: 'var(--red)', color: 'var(--red)' }}>
          {error}
        </div>
      )}

      <div className="card flush">
        <table className="tbl">
          <thead>
            <tr>
              <th>Ref</th>
              <th>Client</th>
              <th>Proposal</th>
              <th>Milestone</th>
              <th className="amt">Amount</th>
              <th>Due</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows === null && (
              <tr><td colSpan={7} style={{ color: 'var(--fg-mute)', fontSize: 13 }}>Loading…</td></tr>
            )}
            {rows !== null && list.length === 0 && (
              <tr>
                <td colSpan={7} style={{ color: 'var(--fg-mute)', fontSize: 13, textAlign: 'center', padding: 32 }}>
                  No invoices in this view.
                </td>
              </tr>
            )}
            {list.map((inv) => (
              <tr
                key={inv.id}
                className="row-clickable"
                tabIndex={0}
                onClick={() => navigate(`/admin/invoices/${inv.id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`/admin/invoices/${inv.id}`);
                  }
                }}
              >
                <td><span className="rid">{inv.id}</span></td>
                <td>{inv.client_name ?? inv.client_id}</td>
                <td style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--fg-soft)' }}>
                  {inv.proposal_id}
                </td>
                <td style={{ color: 'var(--fg-soft)', fontSize: 13 }}>
                  {inv.milestone_label ?? '—'}
                </td>
                <td className="amt" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatMoney(inv.amount_pence, inv.currency)}
                </td>
                <td style={{ color: 'var(--fg-mute)', fontSize: 12, fontFamily: 'var(--mono)' }}>
                  {inv.due_at.slice(0, 10)}
                </td>
                <td>
                  <span className={`chip ${STATUS_TONE[inv.status]}`}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor' }} />
                    {STATUS_LABEL[inv.status]}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
